/**
 * Custom hook to manage display data for async content reference fields
 *
 * This hook handles the loading and updating of display labels for async autocomplete fields.
 * For most fields, it fetches from Redux. For fields in GRAPHQL_FALLBACK_FIELDS, it falls back
 * to GraphQL if not in Redux (because the REST API filters them out or has limits).
 *
 * @module hooks/useAsyncContentReferenceDisplay
 */
import { useEffect } from 'react'
import { filterRecords } from 'services/data'
import { CONTENT_REFERENCE_FIELDS_ASYNC } from 'services/content-references/types'

/**
 * Content reference fields that should use GraphQL fallback when not found in Redux.
 * Add fields here as you migrate them away from REST API pre-loading.
 *
 * Why these fields need GraphQL:
 * - product: REST API filters out products entirely
 * - (add more as needed)
 */
const GRAPHQL_FALLBACK_FIELDS = ['product']

/**
 * Hook to manage display data for async content reference fields
 *
 * @param {Object} params Hook parameters
 * @param {Map} params.content The content being edited
 * @param {Object} params.values Formik values object
 * @param {Map} params.data Redux data state
 * @param {string} params.storeKey Redux store key
 * @param {Function} params.setFieldValue Formik setFieldValue function
 * @param {Function} params.loadContentReferenceList GraphQL lazy query function for content references
 */
export function useAsyncContentReferenceDisplay ({
  content,
  values,
  data,
  storeKey,
  setFieldValue,
  loadContentReferenceList,
}) {
  useEffect(() => {
    let cancelled = false

    // Debounce the expensive operations to prevent overwhelming the browser
    const timeoutId = setTimeout(async () => {
      if (cancelled) return

      const asyncFields = CONTENT_REFERENCE_FIELDS_ASYNC.get(content.get('contentType')) || []

      const fieldUpdates = await Promise.all(
        asyncFields.map(async (field) => {
          const fieldValue = values[field]
          if (!fieldValue || !fieldValue.value) {
            return null
          }

          const ids = Array.isArray(fieldValue.value) ? fieldValue.value : [fieldValue.value]

          // Try to find records in Redux
          const records = filterRecords({ data, storeKey }, (c) => ids.includes(c.get('contentId')))

          let displayData = records
            .map((record) => ({
              label: record.get('title'),
              value: record.get('contentId'),
            }))
            .toArray()

          // If we're missing records and this field uses GraphQL fallback, fetch via GraphQL
          // Some content types are filtered by REST API or have limits, so we use GraphQL
          if (displayData.length < ids.length && GRAPHQL_FALLBACK_FIELDS.includes(field)) {
            try {
              // Fetch missing content by their IDs via GraphQL
              const missingIds = ids.filter((id) => !displayData.some((d) => d.value === id))

              // Fetch content sequentially to avoid race conditions with lazy query
              const fetchedContent = []
              // eslint-disable-next-line no-plusplus
              for (let i = 0; i < missingIds.length; i += 1) {
                if (cancelled) break
                const id = missingIds[i]
                try {
                  // eslint-disable-next-line no-await-in-loop
                  const result = await loadContentReferenceList({
                    variables: {
                      limit: 1,
                      where: { contentId: { eq: id } },
                    },
                  })
                  const contentItem = result?.data?.contentList?.content?.[0]
                  if (contentItem) {
                    fetchedContent.push({
                      label: contentItem.title,
                      value: contentItem.contentId,
                    })
                  }
                } catch (err) {
                  // Skip this item if query fails
                }
              }

              displayData = [...displayData, ...fetchedContent]
            } catch (error) {
              // Silently fail, user can still add/search for content
            }
          }

          const newDisplay = Array.isArray(fieldValue.value) ? displayData : displayData[0] || null

          // Only update if we have display data for ALL selected values (multi) or a new label (single)
          if (
            Array.isArray(fieldValue.value) &&
            displayData.length === fieldValue.value.length &&
            JSON.stringify(fieldValue.display) !== JSON.stringify(displayData)
          ) {
            return { field, value: fieldValue.value, display: displayData }
          }
          if (
            !Array.isArray(fieldValue.value) &&
            newDisplay &&
            JSON.stringify(fieldValue.display) !== JSON.stringify(newDisplay)
          ) {
            return { field, value: fieldValue.value, display: newDisplay }
          }
          return null
        }),
      )

      // Apply all field updates
      if (!cancelled) {
        fieldUpdates
          .filter((update) => update !== null)
          .forEach((update) => {
            setFieldValue(update.field, { value: update.value, display: update.display }, false)
          })
      }
    }, 800) // 800ms debounce delay

    // Cleanup: cancel operations if component unmounts or dependencies change
    return () => {
      cancelled = true
      clearTimeout(timeoutId)
    }
  }, [values, data, content, setFieldValue, storeKey, loadContentReferenceList])
}
